<template>
  <div ref="root" class="rcp" :class="{ 'rcp--focus': focused, 'rcp--invalid': invalid.length }" @click="focusInput">
    <span class="rcp__label">{{ label }}</span>
    <span
      v-for="(entry, index) in modelValue" :key="entry.address + index"
      class="rcp__chip" :class="{ 'rcp__chip--bad': !valid(entry.address) }"
      :title="entry.name ? `${entry.name} <${entry.address}>` : entry.address"
    >
      {{ entry.name || entry.address }}
      <button type="button" class="rcp__x" :aria-label="t('mailClient.compose.removeRecipient', { who: entry.address })" @click.stop="remove(index)">×</button>
    </span>
    <!--
      Teclado de correo y nada que «corrija»: el autocorrector cambiaba «ana@»
      por «Ana@» y la mayúscula inicial hacía lo mismo. `type="text"` con
      `inputmode="email"` y no `type="email"`: aquí también se busca por
      nombre («Marta Vidal», con espacio) y se pega «Ana <ana@x.com>», que un
      campo de tipo email recorta o rechaza. Intro con la caja vacía pasa al
      campo siguiente (`next`); con algo escrito, lo convierte en destinatario.
    -->
    <input
      ref="input"
      v-model="typed"
      class="rcp__input"
      type="text"
      inputmode="email"
      autocomplete="off"
      autocapitalize="off"
      autocorrect="off"
      spellcheck="false"
      enterkeyhint="next"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="open"
      :aria-controls="listId"
      :aria-label="label"
      @focus="onFocus"
      @blur="onBlur"
      @input="lookUp"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="onEnter"
      @keydown.tab="onTab"
      @keydown="onKey"
      @paste="onPaste"
    >
    <!--
      En el teléfono la lista sale del compositor (Teleport) y se coloca dentro
      de lo que se ve por encima del teclado (useSuggestionPopover de ui-kit):
      colgada del campo dentro del compositor se abría debajo de las teclas o
      tapaba la barra de «Enviar». En el escritorio se queda donde estaba.
      Cada fila: mousedown.prevent para que el campo no pierda el foco (y el
      teclado no se cierre) y se elige en el click, que no llega después de un
      arrastre: deslizar el dedo por la lista no elige a nadie, y el click del
      mismo toque no cae en lo que había debajo.
    -->
    <Teleport to="body" :disabled="!floating">
      <ul
        v-if="open && suggestions.length"
        :id="listId"
        ref="list"
        class="rcp__list"
        :class="{ 'rcp__list--floating': floating }"
        :style="floating ? place : null"
        role="listbox"
      >
        <li
          v-for="(entry, index) in suggestions" :key="entry.email + index"
          class="rcp__item" :class="{ 'rcp__item--on': index === cursor }"
          role="option" :aria-selected="index === cursor"
          @mousedown.prevent
          @click="pick(entry)"
          @mouseenter="cursor = index"
        >
          <span class="rcp__name">{{ entry.name || entry.email }}</span>
          <span class="rcp__mail">{{ entry.email }}</span>
          <span v-if="entry.context" class="rcp__ctx">{{ entry.context }}</span>
        </li>
      </ul>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { isTouchScreen, useSuggestionPopover } from '@general/ui-kit'

import { mailApi } from './api'

/**
 * Who a message goes to: chips of { name, address }.
 *
 * Suggestions come from the platform's address book (GET /recipients), where
 * every module that keeps email addresses answers — customers, suppliers,
 * colleagues — without this field knowing any of them. When the message is
 * about a record (`scope`), its own people come first: writing from an invoice,
 * the customer's contacts are what somebody is looking for.
 *
 * Anything typed that looks like an address becomes a chip on comma, Enter,
 * Tab or leaving the field; a chip that is not a valid address is shown in red,
 * and the composer will not send with one.
 */
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  label: { type: String, required: true },
  scope: { type: Object, default: null },
})
// `next`: Intro con la caja vacía, para que el compositor pase al asunto.
const emit = defineEmits(['update:modelValue', 'next'])
const { t } = useI18n()

const EMAIL = /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:]+$/
const valid = address => EMAIL.test(String(address ?? '').trim())

const root = ref(null)
const input = ref(null)
const typed = ref('')
const suggestions = ref([])
const cursor = ref(-1)
const open = ref(false)
const focused = ref(false)
const list = ref(null)
const listId = `rcp-${useId()}`
let timer = null
let round = 0

// Con el dedo, o en la pantalla del teléfono (donde el compositor ocupa todo):
// la lista flota fuera del compositor, contra lo que se ve.
const floating = isTouchScreen() || (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 760px)').matches)
const shown = computed(() => open.value && suggestions.value.length > 0)
const { style: place, reveal } = useSuggestionPopover({
  anchor: root, list, open: shown, onDismiss: () => close(), maxHeight: 280,
})

const invalid = computed(() => props.modelValue.filter(entry => !valid(entry.address)))

function focusInput() {
  input.value?.focus()
}

function add(entries) {
  const current = [...props.modelValue]
  for (const entry of entries) {
    const address = String(entry.address ?? '').trim().toLowerCase()
    if (!address || current.some(item => item.address === address)) continue
    current.push({ name: String(entry.name ?? '').trim(), address })
  }
  emit('update:modelValue', current)
}

function remove(index) {
  const current = [...props.modelValue]
  current.splice(index, 1)
  emit('update:modelValue', current)
}

/** «Ana Pérez <ana@x.com>, luis@y.com» → chips. */
function parse(text) {
  return String(text).split(/[,;\n]+/).map(part => part.trim()).filter(Boolean).map((part) => {
    const match = part.match(/^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/)
    return match ? { name: match[1].trim(), address: match[2].trim() } : { name: '', address: part }
  })
}

/**
 * «@» al principio de la caja: buscar en TODA la libreta aunque el correo sea
 * sobre una ficha (que por defecto sugiere solo a los suyos). Lo de detrás del
 * arroba es lo que se busca. Una dirección de verdad nunca empieza por «@», así
 * que no se confunde con una que se esté tecleando; y por eso lo escrito con
 * arroba delante nunca se convierte en destinatario tal cual.
 */
const everywhere = () => typed.value.trim().startsWith('@')

function commit() {
  if (open.value && cursor.value >= 0 && suggestions.value[cursor.value]) {
    pick(suggestions.value[cursor.value])
    return
  }
  if (everywhere()) {
    // Intro buscando en la libreta: la primera propuesta, si hay alguna.
    if (open.value && suggestions.value.length) pick(suggestions.value[0])
    return
  }
  if (typed.value.trim()) {
    add(parse(typed.value))
    typed.value = ''
  }
  close()
}

function pick(entry) {
  add([{ name: entry.name, address: entry.email }])
  typed.value = ''
  close()
  input.value?.focus()
}

function onKey(event) {
  if (event.key === ',' || event.key === ';') {
    event.preventDefault()
    commit()
  } else if (event.key === 'Backspace' && !typed.value && props.modelValue.length) {
    remove(props.modelValue.length - 1)
  } else if (event.key === 'Escape') {
    close()
  }
}

function onEnter() {
  if (!typed.value.trim() && !(open.value && cursor.value >= 0)) {
    close()
    emit('next')
    return
  }
  commit()
}

function onTab() {
  if (typed.value.trim()) commit()
}

function onPaste(event) {
  const text = event.clipboardData?.getData('text/plain') ?? ''
  if (/[,;\n]/.test(text) || valid(text.trim())) {
    event.preventDefault()
    add(parse(text))
  }
}

function onFocus() {
  focused.value = true
  // Con el teclado fuera, el campo sube a lo alto de lo visible para que la
  // lista tenga sitio debajo (solo con el dedo; en el escritorio no se mueve).
  reveal()
  lookUp()
}

function onBlur() {
  focused.value = false
  setTimeout(() => {
    if (everywhere()) typed.value = ''
    else if (typed.value.trim()) { add(parse(typed.value)); typed.value = '' }
    close()
  }, 120)
}

function move(step) {
  if (!suggestions.value.length) return
  open.value = true
  cursor.value = (cursor.value + step + suggestions.value.length) % suggestions.value.length
}

function close() {
  open.value = false
  cursor.value = -1
}

function lookUp() {
  clearTimeout(timer)
  const whole = everywhere()
  const term = whole ? typed.value.trim().slice(1).trim() : typed.value.trim()
  timer = setTimeout(async () => {
    const mine = ++round
    try {
      const params = { search: term, limit: 8 }
      if (!whole && props.scope?.type && props.scope?.referenceId) Object.assign(params, { scopeType: props.scope.type, scopeId: props.scope.referenceId })
      if (!term && !params.scopeType) { suggestions.value = []; close(); return }
      let data = await mailApi.recipients(params)
      // Nothing inside the record for this term: the whole book.
      if (term && params.scopeType && !(data.items ?? data.recipients ?? []).length) data = await mailApi.recipients({ search: term, limit: 8 })
      if (mine !== round) return
      const taken = new Set(props.modelValue.map(entry => entry.address))
      suggestions.value = (data.items ?? data.recipients ?? []).filter(entry => entry.email && !taken.has(entry.email.toLowerCase()))
      open.value = focused.value && suggestions.value.length > 0
      cursor.value = -1
    } catch {
      suggestions.value = []
    }
  }, term ? 180 : 0)
}

defineExpose({ commit, focus: focusInput })
</script>

<style scoped>
.rcp {
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: 36px;
  padding: 3px 0;
  border-bottom: 1px solid var(--color-border);
  cursor: text;
}
.rcp--focus { border-bottom-color: var(--color-primary); }
.rcp__label { width: 52px; color: var(--color-text-soft); font-size: var(--text-sm); flex-shrink: 0; }
.rcp__chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 260px;
  padding: 2px 4px 2px 10px;
  border-radius: 999px;
  background: var(--color-primary-soft);
  color: var(--color-text);
  font-size: var(--text-sm);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rcp__chip--bad { background: var(--color-danger-soft); color: var(--color-danger); }
.rcp__x { border: 0; background: none; cursor: pointer; color: inherit; font-size: 15px; line-height: 1; padding: 0 4px; border-radius: 50%; }
.rcp__x:hover { background: rgba(0, 0, 0, .08); }
.rcp__input {
  flex: 1;
  min-width: 140px;
  height: 28px;
  border: 0;
  outline: none;
  background: transparent;
  color: var(--color-text);
  font-size: var(--text-sm);
}
.rcp__list {
  position: absolute;
  z-index: 40;
  top: 100%;
  left: 52px;
  right: 0;
  max-height: 280px;
  overflow-y: auto;
  margin: 4px 0 0;
  padding: 4px;
  list-style: none;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}
/* Fuera del compositor (Teleport a <body>): `top`, `left`, `width` y el alto
   que de verdad queda los pone useSuggestionPopover en línea. Por encima del
   compositor (150) y de un diálogo (185), por debajo de los avisos (200), como
   la lista de UiReferenceField. */
.rcp__list--floating {
  position: fixed;
  z-index: 190;
  right: auto;
  margin: 0;
  /* Llegar al final con el dedo no arrastra el compositor de detrás. */
  overscroll-behavior: contain;
}
.rcp__item { display: grid; grid-template-columns: 1fr auto; gap: 0 8px; padding: 6px 8px; border-radius: 6px; cursor: pointer; }
.rcp__item--on { background: var(--color-primary-soft); }
.rcp__name { font-size: var(--text-sm); font-weight: 600; color: var(--color-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rcp__mail { font-size: var(--text-xs); color: var(--color-text-soft); grid-column: 1; overflow: hidden; text-overflow: ellipsis; }
.rcp__ctx { grid-row: 1 / span 2; grid-column: 2; align-self: center; font-size: var(--text-xs); color: var(--color-text-mute); }

/* Con el dedo: la caja a 16 px (más pequeña, iOS amplía la página al enfocarla
   y no la devuelve), filas de 44 px que se aciertan con el pulgar, y la cruz
   de una ficha a 28 px (en línea caben varias por renglón; ver --text-field y
   el mínimo táctil de ui-kit). */
/* En el teléfono la caja cabe al lado de una ficha y no baja de renglón
   dejando un hueco del alto de una línea debajo de cada destinatario. */
@media (max-width: 760px) {
  .rcp__input { min-width: 80px; }
}
@media (pointer: coarse) {
  .rcp { min-height: 44px; }
  .rcp__input { height: 36px; font-size: var(--text-field, 16px); }
  .rcp__item { min-height: 44px; align-content: center; padding: 6px 10px; }
  .rcp__x { min-width: 28px; min-height: 28px; font-size: 18px; }
}
</style>
